import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Coins, Dices, Cookie, Cake, Fish, DollarSign } from "lucide-react";
import { toast } from "sonner";
import FishingEditor from "@/components/FishingEditor";
import RpgPanel from "@/components/RpgPanel";

export default function Games() {
  const [users, setUsers] = useState([]);
  const [username, setUsername] = useState("random_rob");
  const [amount, setAmount] = useState(20);
  const [currency, setCurrency] = useState("cookies");
  const [result, setResult] = useState(null);
  const [reels, setReels] = useState(["🍒", "🍋", "⭐"]);
  const [rolling, setRolling] = useState(false);
  const [gtab, setGtab] = useState("fishing");

  const load = async () => setUsers((await api.get("/users")).data);
  useEffect(() => { load(); }, []);
  const current = users.find((u) => u.username.toLowerCase() === username.toLowerCase());

  const play = async (mode) => {
    setRolling(true);
    setResult(null);
    let spin;
    if (mode === "slots") {
      spin = setInterval(() => setReels([rand(), rand(), rand()]), 80);
    }
    try {
      const res = await api.post(`/games/${mode === "slots" ? "slots" : "gamble"}`, { username, amount: parseInt(amount), currency });
      if (mode === "slots") {
        const m = res.data.result.match(/\[ (.+?) \]/);
        if (m) setReels(m[1].split(" "));
      }
      setResult(res.data.result);
      res.data.result.includes("WON") ? toast.success(res.data.result) : toast.error(res.data.result);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bet failed");
    } finally {
      if (spin) clearInterval(spin);
      setRolling(false);
    }
  };
  const rand = () => ["🍒", "🍋", "🔔", "⭐", "💎", "🍪"][Math.floor(Math.random() * 6)];

  const fish = async () => {
    try {
      const res = await api.post("/games/fish", { username });
      res.data.result.includes("too small") || res.data.result.includes("junk")
        ? toast.error(res.data.result) : toast.success(res.data.result);
      await load();
    } catch (e) { toast.error("Fishing failed"); }
  };
  const sellFish = async () => {
    try {
      const res = await api.post("/games/sellfish", { username, currency });
      res.data.earned > 0 ? toast.success(res.data.result) : toast.error(res.data.result);
      await load();
    } catch (e) { toast.error("Sell failed"); }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl md:text-4xl font-800 font-heading">Games & Gambling</h1>
        <p className="text-muted-foreground mt-1 text-sm">Bet cookies & cupcakes. In chat: <span className="text-primary">!gamble 20</span> or <span className="text-primary">!slots 10</span></p>
      </header>

      <div className="flex gap-2 border-b border-white/10 pb-2" data-testid="games-tabs">
        {[["fishing", "🎣 Fishing & Gambling"], ["rpg", "⚔️ RPG Idle-Battler"]].map(([id, label]) => (
          <button key={id} onClick={() => setGtab(id)} className={`text-sm px-4 py-2 rounded-lg font-600 transition-colors ${gtab === id ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:text-white"}`} data-testid={`games-tab-${id}`}>{label}</button>
        ))}
      </div>

      {gtab === "fishing" && (<>

      <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Viewer</label>
            <Input value={username} onChange={(e) => setUsername(e.target.value)} list="games-users" className="bg-secondary border-white/10 mt-1" data-testid="games-username" />
            <datalist id="games-users">{users.map((u) => <option key={u.id} value={u.username} />)}</datalist>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Bet amount</label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-secondary border-white/10 mt-1" data-testid="games-amount" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Currency</label>
            <div className="flex gap-2 mt-1">
              <button onClick={() => setCurrency("cookies")} data-testid="currency-cookies" className={`flex-1 rounded-lg py-2 text-sm flex items-center justify-center gap-1 transition-colors duration-200 ${currency === "cookies" ? "bg-cookie text-black" : "bg-secondary text-muted-foreground"}`}><Cookie className="w-4 h-4" />Cookies</button>
              <button onClick={() => setCurrency("cupcakes")} data-testid="currency-cupcakes" className={`flex-1 rounded-lg py-2 text-sm flex items-center justify-center gap-1 transition-colors duration-200 ${currency === "cupcakes" ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}><Cake className="w-4 h-4" />Cupcakes</button>
            </div>
          </div>
        </div>
        {current && (
          <p className="text-xs text-muted-foreground mt-3">@{current.username} balance: <span className="text-cookie">{current.cookies} 🍪</span> · <span className="text-primary">{current.cupcakes} 🧁</span></p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Coin flip */}
        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-8 flex flex-col items-center">
          <Coins className={`w-16 h-16 text-cookie ${rolling ? "spin-slow" : ""}`} />
          <h3 className="font-heading font-700 text-xl mt-4">Coin Flip</h3>
          <p className="text-xs text-muted-foreground text-center mt-1">50/50 — double your bet or lose it all.</p>
          <Button onClick={() => play("flip")} disabled={rolling} data-testid="flip-button" className="mt-6 w-full bg-cookie text-black hover:bg-cookie/90 font-semibold rounded-lg">Flip for {amount}</Button>
        </div>

        {/* Slots */}
        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-8 flex flex-col items-center">
          <div className="flex gap-2 text-5xl bg-black/50 rounded-lg px-4 py-3 border border-white/10" data-testid="slots-reels">
            {reels.map((r, i) => <span key={i}>{r}</span>)}
          </div>
          <h3 className="font-heading font-700 text-xl mt-4">Slot Machine</h3>
          <p className="text-xs text-muted-foreground text-center mt-1">2 match = x2 · 3 match = x5</p>
          <Button onClick={() => play("slots")} disabled={rolling} data-testid="slots-button" className="mt-6 w-full bg-primary hover:bg-primary/90 font-semibold rounded-lg"><Dices className="w-4 h-4 mr-2" />Spin for {amount}</Button>
        </div>
      </div>

      {result && (
        <div className="bg-[#0a0a0a] border border-primary/30 rounded-lg p-4 text-center animate-fade-up" data-testid="game-result">
          <p className="text-lg">{result}</p>
        </div>
      )}

      {/* Fishing */}
      <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
              <Fish className="w-7 h-7 text-blue-400" />
            </div>
            <div>
              <h3 className="font-heading font-700 text-xl">Fishing 🎣</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Catch fish and sell them. Undersized catches (&lt;15cm) get a fine!</p>
              {current && <p className="text-xs mt-1">In basket: <span className="text-blue-300 font-semibold">{current.fish?.length || 0} fish</span> worth <span className="text-cookie">{(current.fish || []).reduce((s, f) => s + f.value, 0)}</span></p>}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={fish} data-testid="fish-button" className="bg-blue-500 hover:bg-blue-500/90 rounded-lg"><Fish className="w-4 h-4 mr-2" />Cast Line</Button>
            <Button onClick={sellFish} data-testid="sellfish-button" variant="outline" className="border-white/10 rounded-lg"><DollarSign className="w-4 h-4 mr-1" />Sell for {currency === "cookies" ? "🍪" : "🧁"}</Button>
          </div>
        </div>
        {current && current.fish?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4" data-testid="fish-basket">
            {current.fish.map((f) => (
              <span key={f.id} className="text-sm px-3 py-1.5 rounded-full bg-secondary border border-white/10">{f.emoji} {f.name} · {f.size}cm · {f.value}</span>
            ))}
          </div>
        )}
      </div>
      <FishingEditor />
      </>)}
      {gtab === "rpg" && <RpgPanel />}
    </div>
  );
}
